import type { $Enums } from '@prisma/client';
import { getStatusLabel } from '../../../status-labels';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';

export type LotStatus = $Enums.LotStatus;

export type LotFacts = {
  /** The lot's identity, carried with its policy facts. */
  id: number;
  status: LotStatus;
  /** On-hand units; a lot closes only once nothing is left (`lotCanClose`). */
  quantity: number;
  /** A sales order the lot was sold on has shipped (`lotHasShippedSale`). */
  hasShippedSale: boolean;
  /** Units or weight were already drawn from the lot (`lotIsInUse`). */
  isInUse: boolean;
};

/**
 * The lot fields a surface can write. `warehouse` is the move, `product` the re-product door;
 * `grade` and `initialWeight` carry no status rule on either side of the wire today.
 */
export const LOT_FIELDS = ['warehouse', 'product', 'grade', 'initialWeight'] as const;

export type LotField = (typeof LOT_FIELDS)[number];

/** The lifecycle events (`LotEvent`) plus the quantity operations the lot page offers. */
export const LOT_OPS = ['close', 'reopen', 'lock', 'unlock', 'dump', 'change quantity'] as const;

export type LotOp = (typeof LOT_OPS)[number];

/** The events `transitionLot` accepts; each op id IS its event name. */
export const LOT_LIFECYCLE_EVENTS = ['close', 'reopen', 'lock', 'unlock'] as const;

export type LotLifecycleEvent = (typeof LOT_LIFECYCLE_EVENTS)[number];

/** ONE event → operation mapping, so the api door and the web button ask the same question. */
export const lotLifecycleOperation = (event: LotLifecycleEvent): LotOp => event;

/** A lot can be deleted only before it is received into stock. */
export const LOT_DELETABLE_STATUSES: readonly LotStatus[] = ['DRAFT', 'MID_RECEIVING'];

/** The sales order statuses after which the lot's product is traceability-frozen. */
export const LOT_SHIPPED_SALE_STATUSES: readonly string[] = ['SHIPPED', 'CLOSED'];

/** THE shipped-sale fact, shared so the api projection and any web surface compute it alike (#7630). */
export const lotHasShippedSale = (salesOrderStatuses: readonly (string | null | undefined)[]): boolean =>
  salesOrderStatuses.some((status) => status != null && LOT_SHIPPED_SALE_STATUSES.includes(status));

/** THE in-use fact: any unit or any weight already dispatched, repacked or transferred out. */
export const lotIsInUse = ({ unitsUsed, weightUsed }: { unitsUsed: number; weightUsed: number }): boolean =>
  unitsUsed > 0 || weightUsed > 0;

/** THE close fact: a lot closes once its quantity is at or below zero. */
export const lotCanClose = (quantity: number): boolean => quantity <= 0;

/**
 * THE CLOSE/REOPEN MACHINE (api/src/services/lots/lot.states.ts) IS THE AUTHORITY ON THOSE EVENTS.
 *
 *   open    { close }    close also needs quantity ≤ 0
 *   closed  { reopen }
 *   draft · mid receiving · mid production run · locked   the machine does not model them
 *
 * lock and unlock run outside the machine: lock only from OPEN, unlock only from LOCKED.
 */
const MACHINE_OPS: Partial<Record<LotStatus, readonly LotOp[]>> = { OPEN: ['close'], CLOSED: ['reopen'] };

const ACTION_LABELS = new Map<string, string>([
  ['close', 'Close Lot'],
  ['reopen', 'Reopen Lot'],
]);

export const lotInvalidEvent = (op: string, status: LotStatus) =>
  deny(
    'LOT_INVALID_EVENT',
    `${ACTION_LABELS.get(op) ?? op} isn't available while the lot is ${getStatusLabel(status)}.`
  );

const decideOperation = (facts: LotFacts, op: LotOp): Decision => {
  switch (op) {
    case 'close':
    case 'reopen':
      if (!(MACHINE_OPS[facts.status] ?? []).includes(op)) return lotInvalidEvent(op, facts.status);
      if (op === 'close' && !lotCanClose(facts.quantity)) {
        return deny('LOT_CLOSE_QUANTITY_REMAINING', "Can't close: the lot still has quantity.");
      }
      return allow();
    case 'lock':
      return facts.status === 'OPEN' ? allow() : deny('LOT_LOCK_NOT_OPEN', 'Only Open lots can be locked.');
    case 'unlock':
      return facts.status === 'LOCKED' ? allow() : deny('LOT_UNLOCK_NOT_LOCKED', 'Only Locked lots can be unlocked.');
    case 'dump':
      return facts.status === 'OPEN' ? allow() : deny('LOT_QUANTITY_LOCKED', 'Only Open lots can be dumped.');
    case 'change quantity':
      return facts.status === 'OPEN' ? allow() : deny('LOT_QUANTITY_LOCKED', 'Only Open lots can be adjusted.');
  }
};

type LotAction = Action<LotField, LotOp>;

const decideEntity = (facts: LotFacts, action: LotAction): Decision => {
  switch (action.type) {
    case 'view':
    case 'add':
    case 'edit':
      return allow();
    case 'delete':
      if (!LOT_DELETABLE_STATUSES.includes(facts.status)) {
        return deny('LOT_DELETE_LOCKED', 'Only Draft or Mid Receiving lots can be deleted.');
      }
      return facts.isInUse ? deny('LOT_IN_USE', "Can't delete: this lot is used on an order or run.") : allow();
    case 'perform':
      return decideOperation(facts, action.op);
  }
};

const ungoverned = (): Decision => allow();

const fieldRules = {
  warehouse: (facts: LotFacts, action: LotAction): Decision =>
    action.type !== 'edit' || facts.status === 'OPEN'
      ? allow()
      : deny('LOT_MOVE_LOCKED', 'Only Open lots can be moved.'),
  product: (facts: LotFacts, action: LotAction): Decision =>
    action.type !== 'edit' || !facts.hasShippedSale
      ? allow()
      : deny('LOT_PRODUCT_LOCKED', 'Product is locked: this lot is on a shipped sales order.'),
  grade: ungoverned,
  initialWeight: ungoverned,
} satisfies Record<LotField, (facts: LotFacts, action: LotAction) => Decision>;

export const lotPolicy: Policy<LotFacts, LotField, LotOp> = definePolicy<LotFacts, LotField, LotOp>({
  kind: 'lot',
  fields: LOT_FIELDS,
  ops: LOT_OPS,
  requires: {},
  entity: decideEntity,
  field: fieldRules,
});
